import React from 'react';
import PropTypes from 'prop-types';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import map from 'lodash/map';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import { fromGlobalId } from 'graphql-relay';
import Button from '../../components/Button';
import Grid from '../../components/Grid';
import messages from './messages';
import './styles.css';

export const getLeadingMonthOrDayZero = (numToFormat) => (`0${(numToFormat)}`.slice(-2));

const getEventTypeText = (event) => {
  if (event.type === 'Visit') {
    const Message = (props) => (
      <FormattedMessage
        {...props}
        values={{
          type: event.type,
        }}
      />
    );
    if (event.visitLocationType === 'VIRTUAL') {
      return <Message {...messages.virtual} />;
    }
    return <Message {...messages.inPerson} />;
  }
  return event.type;
};

const OverviewResults = ({ showSideBar, event, isCommunityServerlessOn }) => {
  const globalId = event.highSchoolId && fromGlobalId(event.highSchoolId).id;

  return (
    <Grid styleName="event-grid" id="overview-grid">
      <Grid.Row styleName="event-row" data-cy="overview-results-rowlist">
        <Grid.Column width={2}>
          <div styleName="time-column">
            <div styleName="time-big">{event.time}</div>
            <div styleName="time-timezone">{event.timezone}</div>
            <div data-cy="overview-results-visit-type" id="overview-results-visit-type">
              {getEventTypeText(event)}
            </div>
          </div>
        </Grid.Column>
        <Grid.Column width={5}>
          {
            isCommunityServerlessOn ?
              <Link to={`/counselorcommunity/search/institutions/highschools/${globalId}`} styleName="name-link">{event.name}</Link> :
              <Link to={`/counselor-community/institution/${event.institutionId}`} styleName="name-link">{event.name}</Link>
          }
          <div>{event.start} - {event.end}</div>
        </Grid.Column>
        <Grid.Column width={7}>
          <div>
            {
              isCommunityServerlessOn ?
                <Link to={`/counselorcommunity/search/people/${event?.communityId}`}>{event.contact}</Link> : (
                  <Link to={`/counselor-community/profile/${event.communityId}`}>{event.contact}</Link>
                )
            }
          </div>
          <span>{event.phone}</span>{' '}
          <Link href={`mailto:${event.email}`}>
            <span>{event.email}</span>
          </Link>
        </Grid.Column>
        <Grid.Column width={2}>
          <Button
            name="decline-college-attendee"
            onClick={() => showSideBar(event)}
            styleName="to-uppercase"
          >
            <FormattedMessage {...messages.details} />
          </Button>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

OverviewResults.propTypes = {
  showSideBar: PropTypes.func.isRequired,
  event: PropTypes.object.isRequired,
  isCommunityServerlessOn: PropTypes.bool.isRequired,
};

function setTime(time) {
  const index = time.indexOf(':');
  time = `${time.slice(0, index+3)} ${time.slice(index+3)}`;
  return time.toUpperCase();
}

function createEventObject(event) {
  const highSchool = get(event, 'highSchool') || null;
  const rvAccount = highSchool ? highSchool.repVisitsAccount : null;
  let fairContact = null;
  let primaryContact = null;
  if (rvAccount && rvAccount.collegeFairsPrimaryContact) {
    fairContact = {};
    fairContact.name = rvAccount.collegeFairsPrimaryContact.name;
    fairContact.email = rvAccount.collegeFairsPrimaryContact.field_contact_work;
    fairContact.communityId = rvAccount.collegeFairsPrimaryContact.communityId;
    fairContact.institutionId = rvAccount.collegeFairsPrimaryContact.field_user_institution;
    if (rvAccount.collegeFairsPrimaryContactPhone) {
      fairContact.phone = rvAccount.collegeFairsPrimaryContactPhone;
    }
  }
  if (rvAccount && rvAccount.primaryContact) {
    primaryContact = {};
    primaryContact.name = rvAccount.primaryContact.name;
    primaryContact.email = rvAccount.primaryContact.field_contact_work;
    primaryContact.communityId = rvAccount.primaryContact.communityId;
    primaryContact.institutionId = rvAccount.primaryContact.field_user_institution;
    if (rvAccount.primaryContactPhone) {
      primaryContact.phone = rvAccount.primaryContactPhone;
    }
  }
  let contact = primaryContact;
  if ((!!get(event, 'collegeFairId') && !!fairContact) || !primaryContact) {
    contact = fairContact;
  }

  let time = get(event, 'startTime') || '';
  const index = time.indexOf(':');
  const subStringTime = time.substring(index, index+3);
  if (subStringTime === ':00') {
    time = time.replace(subStringTime, ' ');
  } else {
    time = `${time.slice(0, index+3)} ${time.slice(index+3)}`;
  }
  const newEvent = {
    id: get(event, 'id'),
    date: get(event, 'date') || '',
    type: get(event, 'collegeFairId') ? 'Fair' : 'Visit',
    name: highSchool && highSchool.name ? highSchool.name : '',
    highSchoolId: highSchool && highSchool.id ? highSchool.id : '',
    time: time.toUpperCase(),
    start: setTime(get(event, 'startTime')) || '',
    end: setTime(get(event, 'endTime')) || '',
    timezone: get(event, 'timezone') || '',
    contact: contact && contact.name ? contact.name : '',
    email: contact && contact.email ? contact.email : '',
    phone: contact && contact.phone ? contact.phone : '',
    communityId: contact && contact.communityId ? contact.communityId : '',
    institutionId: contact && contact.institutionId ? contact.institutionId : '',
    appointmentId: event.appointmentId,
    dayId: event.dayId,
    collegeFairId: event.collegeFairId,
    visitLocationType: get(event, 'chosenVisitLocationType', 'IN_PERSON'),
  };
  return newEvent;
}

function getEventsByDay(allEvents) {
  let events = groupBy(allEvents, 'date');
  events = map(events, (value, key) => ({ date: key, events: value }));
  return events;
}

function formatDate(date) {
  return `${date.getFullYear()}-${getLeadingMonthOrDayZero(date.getMonth()+1)}-${getLeadingMonthOrDayZero(date.getDate())}`;
}

class RepVisitsOverviewResults extends React.PureComponent {
  static propTypes = {
    showSideBar: PropTypes.func.isRequired,
    repVisitsEvents: PropTypes.array,
    isCommunityServerlessOn: PropTypes.bool,
  };

  setEventsArrays() {
    const allEvents = this.props.repVisitsEvents.map((repVisitsEvent) => createEventObject(repVisitsEvent));
    return getEventsByDay(allEvents);
  }

  render() {
    const { showSideBar, isCommunityServerlessOn } = this.props;
    const dates = this.setEventsArrays();
    const today = formatDate(new Date());
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate()+1);
    const tomorrow = formatDate(tomorrowDate);
    return (
      <div>
        <h1 styleName="overview-results-header"><FormattedMessage {...messages.yourUpcomingAppointments} /></h1>
        {dates.map((date) => (
          <div key={date.date} styleName="events-by-day-container">
            <div styleName="date-header">
              {date.date === today &&
                (<h4 styleName="date-header-row"><FormattedMessage {...messages.today} /></h4>)}
              {date.date === tomorrow &&
                <h4 styleName="date-header-row"><FormattedMessage {...messages.tomorrow} /></h4>}
              {date.date > tomorrow && (
              <h4 styleName="date-header-row"><FormattedDate
                value={date.date}
                weekday="long"
                month="long"
                day="numeric"
                year="numeric"
              />
              </h4>
              )}
            </div>
            {date.events.map((repVisitsEvent) => (
              <OverviewResults
                key={repVisitsEvent.id}
                showSideBar={showSideBar}
                event={repVisitsEvent}
                isCommunityServerlessOn={isCommunityServerlessOn}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }
}

export default RepVisitsOverviewResults;
